import { motion } from 'framer-motion'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/state/auth'
import { useWallet } from '@/state/wallet'
import { useServices } from '@/state/services'
import { useReferrals } from '@/state/referrals'
import { DollarSign, Users, ShoppingCart, TrendingUp, ArrowRight, Activity } from 'lucide-react'
import { BackgroundGradient } from '@/components/ui/shadcn-io/background-gradient'
import { useTranslation } from '@/hooks/useTranslation'
import { useNavigate } from '@tanstack/react-router'
import { useDesignSystem } from '@/hooks/useDesignSystem'
import { FearGreedWidget } from '@/components/widgets/fear-greed-widget'

export default function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { wallet, dataIsLoading: walletLoading } = useWallet()
  const { userSubscriptions, dataIsLoading: servicesLoading } = useServices()
  const { referralSummary, dataIsLoading: referralsLoading } = useReferrals()
  const { animations, patterns } = useDesignSystem()

  const stats = [
    {
      title: t('pages.dashboard.stats.totalBalance'),
      value: walletLoading ? t('common.messages.loading') : `${wallet?.balance || '0'} ${wallet?.currency || 'USD'}`,
      icon: DollarSign,
      subtitle: t('pages.dashboard.stats.walletSubtitle')
    },
    {
      title: t('pages.dashboard.stats.activeServices'),
      value: servicesLoading ? t('common.messages.loading') : userSubscriptions?.length || 0,
      icon: ShoppingCart,
      subtitle: t('pages.dashboard.stats.servicesSubtitle')
    },
    {
      title: t('pages.dashboard.stats.totalReferrals'),
      value: referralsLoading ? t('common.messages.loading') : referralSummary?.total_referrals || 0,
      icon: Users,
      subtitle: t('pages.dashboard.stats.referralsSubtitle')
    },
    {
      title: t('pages.dashboard.stats.totalCommission'),
      value: referralsLoading ? t('common.messages.loading') : `${referralSummary?.total_commission_earned || '0'} USD`,
      icon: TrendingUp,
      subtitle: t('pages.dashboard.stats.commissionSubtitle')
    }
  ]
  const navigator = useNavigate()
  return (
    <motion.div
      className={patterns.layout.container + ' py-6 space-y-8'}
      initial="initial"
      animate="animate"
      variants={animations.page.fadeInUp}
    >
      {/* Header */}
      <motion.div
        variants={animations.content.header}
        className="space-y-4"
      >
        <BackgroundGradient className={'p-6 pt-8' + patterns.radius['3xl']}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={'text-4xl font-bold tracking-tight mb-2 ' + patterns.text.gradient}>
                {t('pages.dashboard.welcome', { name: user?.name || t('common.user') })}
              </h1>
              <p className={patterns.text.subheading}>
                {t('pages.dashboard.todayActivity')}
              </p>
            </div>
            <motion.div
              variants={animations.stats.counter}
              className="animate-bounce-gentle"
            >
              <Activity className="h-12 w-12 text-primary" />
            </motion.div>
          </div>
        </BackgroundGradient>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        className={patterns.layout.cardGrid}
        variants={animations.component.staggerContainer}
        initial="hidden"
        animate="visible"
      >
        {stats.map((stat) => (
          <motion.div
            key={stat.title}
            variants={animations.stats.statsCard}
            whileHover={{ y: -4, transition: { duration: 0.15 } }}
            className="h-full"
          >
            <Card className={'p-6 h-full group cursor-pointer ' + patterns.card.base}>
              <div className="flex items-start justify-between mb-4">
                <div className="space-y-2">
                  <p className={'text-sm font-medium ' + patterns.text.muted}>{stat.title}</p>
                  <p className="text-3xl font-bold tracking-tight">{stat.value}</p>
                </div>
                <div className={patterns.icon.lg + ' ' + patterns.icon.primary}>
                  <stat.icon className="h-7 w-7 text-primary" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className={'text-sm font-medium text-muted-foreground ' + patterns.radius.lg}>
                  {stat.subtitle}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-200" />
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Content Grid */}
      <div className={patterns.layout.contentGrid}>
        {/* Fear and Greed Index */}
        <motion.div
          variants={animations.content.section}
          className="lg:col-span-2"
        >
          <FearGreedWidget />
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          variants={animations.content.section}
        >
          <Card className={'p-6 ' + patterns.card.base}>
            <h3 className="text-xl font-semibold mb-6">{t('pages.dashboard.quickActions')}</h3>
            <motion.div
              className="space-y-3"
              variants={animations.component.staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {[
                { label: t('pages.dashboard.actions.browseServices'), icon: ShoppingCart, link: '/dashboard/services/browse' },
                { label: t('pages.dashboard.actions.viewReferrals'), icon: Users },
                { label: t('pages.dashboard.actions.manageWallet'), icon: DollarSign },
                { label: t('pages.dashboard.actions.viewAnalytics'), icon: TrendingUp },
              ].map((action) => (
                <motion.div
                  key={action.label}
                  variants={animations.component.listItem}
                  whileHover={{ x: 4, transition: { duration: 0.15 } }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="ghost"
                    className="w-full justify-start p-4 h-auto group"
                    onClick={() => action.link ? navigator({ to: action.link }) : null}
                  >
                    <action.icon className="h-5 w-5 mr-3 text-primary group-hover:scale-110 transition-transform" />
                    <span className="font-medium">{action.label}</span>
                    <ArrowRight className="ml-auto h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </Button>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              variants={animations.content.section}
              className="mt-6 pt-6 border-t border-border"
            >
              <Button variant="filament" className="w-full">
                {t('pages.dashboard.upgradeAccount')}
              </Button>
            </motion.div>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  )
}
